// src/components/modals/AddUserModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { X, Loader2, User, Shield, Building, Key, Eye, EyeOff } from 'lucide-react'
import UserService from '../../services/userService'
import BranchService from '../../services/branchService'
import { validatePassword } from '../../utils/passwordValidation'

const AddUserModal = ({ isOpen, onClose, onUserAdded }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: '',
    branchId: '',
    branchIdManual: '',
    isActive: true
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [branches, setBranches] = useState([])
  const [branchesLoading, setBranchesLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Get current logged-in user's role
  const getCurrentUserRole = () => {
    try {
      const userData = localStorage.getItem('tezlaa-user')
      if (userData) {
        const user = JSON.parse(userData)
        return user.role
      }
    } catch (error) {
      console.error('Error getting current user role:', error)
    }
    return null
  }

  // Filter roles based on current user's role
  const getFilteredRoles = () => {
    const allRoles = UserService.getUserRoles()
    const currentUserRole = getCurrentUserRole()

    // If no user role found, return empty array (shouldn't happen)
    if (!currentUserRole) {
      return []
    }

    // Super admin can see all roles
    if (currentUserRole === 'super_admin') {
      return allRoles
    }

    // Admin can create manager, cashier, barista (not admin or super_admin)
    if (currentUserRole === 'admin') {
      return allRoles.filter(role => 
        role.value !== 'admin' && role.value !== 'super_admin'
      )
    }

    // Manager can create cashier and barista (not admin, super_admin, or manager)
    if (currentUserRole === 'manager') {
      return allRoles.filter(role => 
        role.value !== 'admin' && 
        role.value !== 'super_admin' && 
        role.value !== 'manager'
      )
    }

    // Other roles (cashier, barista) cannot create any users
    return []
  }

  // Get filtered user roles based on logged-in user
  const userRoles = getFilteredRoles()

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        username: '',
        password: '',
        confirmPassword: '',
        role: '',
        branchId: '',
        branchIdManual: '',
        isActive: true
      })
      setErrors({})
      fetchBranches()
    }
  }, [isOpen])

  // Fetch branches for dropdown
  const fetchBranches = async () => {
    try {
      setBranchesLoading(true)
      const response = await BranchService.getAllBranches()
      
      if (response && response.success) {
        const branchesData = response.data?.branches || []
        // Only show active branches
        const activeBranches = branchesData.filter(branch => branch.isActive)
        setBranches(activeBranches)
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
      setBranches([])
    } finally {
      setBranchesLoading(false)
    }
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required'
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters'
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores'
    }

    // Password validation using utility
    const passwordValidation = validatePassword(formData.password)
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.errors[0] // Show first error
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = 'Role is required'
    }

    // Branch validation (required for non-admin and non-super_admin roles)
    if (formData.role !== 'admin' && formData.role !== 'super_admin') {
      if (!formData.branchId && !formData.branchIdManual) {
        newErrors.branchId = 'Branch is required for this role'
        newErrors.branchIdManual = 'Please select a branch or enter branch ID'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      console.log('❌ Form validation failed:', errors)
      return
    }

    try {
      setLoading(true)
      
      // Prepare data for API
      const userData = {
        username: formData.username.trim(),
        password: formData.password,
        role: formData.role
      }
      
      // Only add branchId if it's not admin/super_admin and a branch is selected
      if (formData.role !== 'admin' && formData.role !== 'super_admin') {
        // Use manual branchId if provided, otherwise use dropdown selection
        const branchIdToUse = formData.branchIdManual.trim() || formData.branchId
        if (branchIdToUse) {
          userData.branchId = branchIdToUse
        }
      }
      
      console.log('📤 Creating new user:', {
        ...userData,
        password: '***' // Don't log password
      })
      
      const response = await UserService.createUser(userData)
      
      if (response.success) {
        console.log('✅ User created successfully:', response)
        
        // Reset form
        setFormData({
          username: '',
          password: '',
          confirmPassword: '',
          role: '',
          branchId: '',
          branchIdManual: '',
          isActive: true
        })
        setErrors({})
        
        // Notify parent component
        const newUser = response.data?.user || response.data
        onUserAdded(newUser)
        
        // Close modal
        onClose()
      } else {
        console.log('⚠️ Creation failed with response:', response)
        // Handle API error - show exact backend error message
        if (response.errors) {
          setErrors(response.errors)
        } else {
          // Display the exact error message from backend
          setErrors({ general: response.message || 'Failed to create user. Please try again.' })
        }
      }
    } catch (error) {
      console.error('❌ Error creating user:', error)
      
      // Display the exact error message from backend
      let errorMessage = error.message || 'Failed to create user. Please try again.'
      
      // If error message is generic, try to provide more context
      if (errorMessage === 'Failed to create user. Please try again.' || errorMessage === 'Network Error') {
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.message.includes('duplicate') || error.message.includes('already exists')) {
          errorMessage = 'A user with this username already exists.'
        } else if (error.message.includes('Unauthorized')) {
          errorMessage = 'You are not authorized to create users. Please login again.'
        } else if (error.message.includes('Network')) {
          errorMessage = 'Network error. Please check your connection and try again.'
        }
      }
      
      setErrors({ general: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-gray-200">
          <div className="flex items-center gap-3">
            {/* <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-blue-600" />
            </div> */}
            <h2 className="text-xl font-semibold text-gray-900">Add New User</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-4 space-y-4">
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {errors.general}
            </div>
          )}

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username *
            </label>
            <Input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="eg.akash@branchA"
              leftIcon={User}
              className={errors.username ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
            />
            {errors.username && (
              <p className="mt-1 text-sm text-red-600">{errors.username}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Password"
                className={`w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password *
            </label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Confirm Password"
                className={`w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.confirmPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Role Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role Type *
            </label>
            {userRoles.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md">
                <p className="text-sm">You do not have permission to create users.</p>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white ${
                      errors.role ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                  >
                    <option value="">Select role</option>
                    {userRoles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">{errors.role}</p>
                )}
              </>
            )}
          </div>

          {/* Branch Selection (for non-admin and non-super_admin roles) */}
          {formData.role && formData.role !== 'admin' && formData.role !== 'super_admin' && (
            <>
              {/* Branch Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Branch *
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                  <select
                    name="branchId"
                    value={formData.branchId}
                    onChange={handleInputChange}
                    disabled={branchesLoading}
                    className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white ${
                      errors.branchId ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                    } ${branchesLoading ? 'bg-gray-50' : ''}`}
                  >
                    <option value="">
                      {branchesLoading ? 'Loading branches...' : 'Select branch'}
                    </option>
                    {branches.map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    {branchesLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    ) : (
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </div>
                {errors.branchId && (
                  <p className="mt-1 text-sm text-red-600">{errors.branchId}</p>
                )}
              </div>

              {/* Manual Branch ID Input */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Or Enter Branch ID Manually
                </label>
                <Input
                  type="text"
                  name="branchIdManual"
                  value={formData.branchIdManual}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder="68740b774dbbf43330df0eb2"
                  leftIcon={Building}
                  className={errors.branchIdManual ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.branchIdManual && (
                  <p className="mt-1 text-sm text-red-600">{errors.branchIdManual}</p>
                )}
              </div> */}
            </>
          )}

          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Status</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
              {/* <span className="ml-3 text-sm text-gray-600">
                {formData.isActive ? 'Active' : 'Inactive'}
              </span> */}
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || userRoles.length === 0}
            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create User'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AddUserModal